import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Collapse, 
  IconButton, 
  Divider,
  Paper,
  Tooltip,
  CircularProgress,
  Alert,
  Badge,
  ListItemButton,
  ImageList,
  ImageListItem,
  Drawer,
  Button,
  Switch,
  Grid,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon as MListItemIcon,
  ListItemText as MListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PreviewIcon from '@mui/icons-material/Preview';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import SortIcon from '@mui/icons-material/Sort';
import { ICollection, IAsset } from '../types/damTypes';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import axiosInstance from '../services/axiosInstance';

type SortMode = 'name' | 'updated' | 'count';

interface CollectionSidebarProps {
  collections: ICollection[];
  loading: boolean;
  error: Error | null;
  onCollectionSelected: (collectionId: string | null) => void;
  onCreateCollection: () => void;
  onShareCollection: (collection: ICollection) => void;
  onDeleteCollection: (collection: ICollection) => void;
  selectedCollectionId: string | null;
  currentUserId?: string;
  onDrop?: (collectionId: string, assetIds: string[]) => Promise<void>;
  onToggleCollapsed?: () => void;
  isCollapsed?: boolean;
  /**
   * Extra bottom padding (px) for fixed overlays (e.g. multi-select bar).
   * Used to keep the last collections reachable when the viewport is short.
   */
  bottomInsetPx?: number;
  /**
   * Admin-only: allow toggling a support view that includes ALL users' collections
   * (including private ones) in the sidebar list.
   */
  isAdmin?: boolean;
  adminShowAllCollections?: boolean;
  onAdminShowAllCollectionsChange?: (next: boolean) => void;
}

// Define custom props for the styled component
interface DroppableAreaProps {
  isDragOver?: boolean;
  isSelected?: boolean;
  rowIndex?: number;
}

const thinScrollbarSx = {
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,0.22)',
    borderRadius: '999px',
    border: '2px solid transparent',
    backgroundClip: 'content-box',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(0,0,0,0.32)',
  },
} as const;

// Styled component for the droppable area
const DroppableArea = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isDragOver' && prop !== 'isSelected' && prop !== 'rowIndex'
})<DroppableAreaProps>(({ theme, isDragOver, isSelected }) => ({
  marginBottom: 0,
  borderRadius: '6px',
  transition: 'all 0.15s ease',
  backgroundColor: isDragOver
    ? 'rgba(7, 47, 83, 0.06)'
    : 'transparent',
  border: isDragOver
    ? '1.5px dashed #072f53'
    : isSelected
      ? '1.5px dashed rgba(7, 47, 83, 0.35)'
      : '1.5px solid transparent',
  '&:hover': {
    backgroundColor: isDragOver ? 'rgba(7, 47, 83, 0.06)' : 'rgba(0, 0, 0, 0.03)',
  },
  py: 0.5,
  px: 1
}));

const CollectionSidebar: React.FC<CollectionSidebarProps> = ({
  collections,
  loading,
  error,
  onCollectionSelected,
  onCreateCollection,
  onShareCollection,
  onDeleteCollection,
  selectedCollectionId,
  currentUserId,
  onDrop,
  onToggleCollapsed,
  isCollapsed = false,
  bottomInsetPx = 0,
  isAdmin = false,
  adminShowAllCollections = false,
  onAdminShowAllCollectionsChange
}) => {
  const [expandedCollections, setExpandedCollections] = useState<Record<string, boolean>>({});
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [collectionAssets, setCollectionAssets] = useState<{[key: string]: IAsset[]}>({});
  const [loadingAssets, setLoadingAssets] = useState<{[key: string]: boolean}>({});
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCollectionId, setMenuCollectionId] = useState<string | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameDescription, setRenameDescription] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const [pinnedCollectionIds, setPinnedCollectionIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('dam-pinned-collections');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const [recentlyUsedIds, setRecentlyUsedIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('dam-recently-used-collections');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const [showEmptyInMine, setShowEmptyInMine] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [, forceUpdate] = useState(0);
  const [sortMode, setSortMode] = useState<SortMode>('name');

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePin = (collectionId: string) => {
    setPinnedCollectionIds(prev => {
      const next = prev.includes(collectionId)
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId];
      localStorage.setItem('dam-pinned-collections', JSON.stringify(next));
      return next;
    });
  };

  const addRecentlyUsed = (collectionId: string) => {
    setRecentlyUsedIds(prev => {
      const next = [collectionId, ...prev.filter(id => id !== collectionId)].slice(0, 5);
      localStorage.setItem('dam-recently-used-collections', JSON.stringify(next));
      return next;
    });
  };

  const getOwnerId = (c: ICollection): string | undefined => {
    if (!c.owner) return undefined;
    return typeof c.owner === 'object' ? (c.owner as any)?._id : c.owner;
  };

  const isOwnedByMe = (c: ICollection) => !!currentUserId && getOwnerId(c) === currentUserId;

  const isCollectionPublic = (c: ICollection): boolean => {
    const anyC: any = c as any;
    if (typeof anyC?.isPublicInternal === 'boolean') return anyC.isPublicInternal;
    if (typeof anyC?.isPublic === 'boolean') return anyC.isPublic;
    return false;
  };

  // Backwards compatible: legacy collections without owner are treated as "allmän"
  const isEffectivelyPublic = (c: ICollection): boolean => {
    const anyC: any = c as any;
    return isCollectionPublic(c) || !anyC?.owner;
  };

  const getVisibilityLabel = (c: ICollection): 'Allmän' | 'Privat' => (isEffectivelyPublic(c) ? 'Allmän' : 'Privat');

  const toggleCollectionExpanded = (collectionId: string, sectionKey: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const expandKey = `${sectionKey}-${collectionId}`;
    const isCurrentlyExpanded = Boolean(expandedCollections[expandKey]);
    if (!isCurrentlyExpanded && !collectionAssets[collectionId]) {
      fetchCollectionAssets(collectionId);
    }
    setExpandedCollections(prev => ({
      ...prev,
      [expandKey]: !prev[expandKey]
    }));
  };
  
  const getAssetCount = (c: ICollection) => c.assetCount ?? c.assets?.length ?? 0;

  const sortCollections = (list: ICollection[]): ICollection[] => {
    return [...list].sort((a, b) => {
      switch (sortMode) {
        case 'updated':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case 'count':
          return getAssetCount(b) - getAssetCount(a);
        case 'name':
        default:
          return a.name.localeCompare(b.name, 'sv');
      }
    });
  };

  const cycleSortMode = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSortMode(prev => {
      const modes: SortMode[] = ['name', 'updated', 'count'];
      return modes[(modes.indexOf(prev) + 1) % modes.length];
    });
  };

  const sortLabel: Record<SortMode, string> = {
    name: 'Namn A\u2013Ö',
    updated: 'Senast ändrad',
    count: 'Antal tillgångar',
  };

  const fetchCollectionAssets = async (collectionId: string) => {
    setLoadingAssets(prev => ({ ...prev, [collectionId]: true }));
    try {
      const resp = await axiosInstance.get(`/api/collections/${collectionId}`);
      const col = resp.data;
      const populated = Array.isArray(col.assets)
        ? col.assets.filter((a: any) => typeof a === 'object' && a !== null && a._id) as IAsset[]
        : [];
      setCollectionAssets(prev => ({ ...prev, [collectionId]: populated }));
    } catch {
      setCollectionAssets(prev => ({ ...prev, [collectionId]: [] }));
    } finally {
      setLoadingAssets(prev => ({ ...prev, [collectionId]: false }));
    }
  };
  
  const handleDragOver = (event: React.DragEvent, sectionKey: string, collectionId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverKey(`${sectionKey}-${collectionId}`);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverKey(null);
  };

  const handleDrop = async (event: React.DragEvent, collectionId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverKey(null);
    
    // Parse the dragged asset IDs
    try {
      const assetIds = JSON.parse(event.dataTransfer.getData('application/json'));
      if (onDrop && Array.isArray(assetIds) && assetIds.length > 0) {
        await onDrop(collectionId, assetIds);
        const isAnyExpanded = Object.keys(expandedCollections).some(
          k => k.endsWith(`-${collectionId}`) && expandedCollections[k]
        );
        if (isAnyExpanded) {
          fetchCollectionAssets(collectionId);
        }
      }
    } catch (err) {
      console.error('Error parsing dragged asset data:', err);
    }
  };
  
  const handleCollectionClick = (collectionId: string, sectionKey?: string) => {
    const isSameSelection = selectedCollectionId === collectionId && selectedSection === sectionKey;
    if (!isSameSelection && collectionId) {
      addRecentlyUsed(collectionId);
    }
    if (isSameSelection) {
      setSelectedSection(null);
      onCollectionSelected(null);
    } else {
      setSelectedSection(sectionKey || null);
      onCollectionSelected(collectionId);
    }
  };
  
  // Handle viewing collection details
  const handleViewCollection = (collectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Öppna samlingen med onCollectionSelected istället för att öppna en ny webbsida
    onCollectionSelected(collectionId);
  };
  
  const renderCollectionRow = (collection: ICollection, sectionKey: string) => {
    const expandKey = `${sectionKey}-${collection._id}`;
    const isExpanded = Boolean(expandedCollections[expandKey]);
    const isSelected = collection._id === selectedCollectionId && selectedSection === sectionKey;
    const isDragOver = dragOverKey === `${sectionKey}-${collection._id}`;
    const assets = collectionAssets[collection._id] || [];
    const isLoading = loadingAssets[collection._id] || false;

    return (
      <React.Fragment key={`${sectionKey}-${collection._id}`}>
        <DroppableArea
          onClick={() => handleCollectionClick(collection._id, sectionKey)}
          onDragOver={(e) => handleDragOver(e, sectionKey, collection._id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, collection._id)}
          onContextMenu={(e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            setMenuAnchorEl(e.currentTarget);
            setMenuCollectionId(collection._id);
          }}
          isDragOver={isDragOver}
          isSelected={isSelected}
          sx={{
            ml: 1,
            mr: 0.5,
            pl: 1.25,
            py: 0.6,
            '& .row-actions': { opacity: 0, transition: 'opacity 0.15s ease' },
            '&:hover .row-actions': { opacity: 1 },
          }}
        >
          <Tooltip title={collection.name} placement="top" enterDelay={600}>
            <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: isSelected ? '#072f53' : 'text.primary',
                  fontSize: '0.82rem',
                }}
              >
                {collection.name}
              </Typography>
            </Box>
          </Tooltip>
          <Box
            sx={{
              bgcolor: isSelected ? '#072f53' : 'transparent',
              color: isSelected ? 'white' : '#999',
              borderRadius: '10px',
              fontSize: '0.72rem',
              fontWeight: isSelected ? 600 : 500,
              minWidth: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0.5,
              flexShrink: 0,
            }}
          >
            {getAssetCount(collection)}
          </Box>
          <Box className="row-actions" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 0.25 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchorEl(e.currentTarget);
                setMenuCollectionId(collection._id);
              }}
              sx={{ p: 0.25, color: '#aaa', '&:hover': { color: '#072f53' } }}
            >
              <MoreVertIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {getAssetCount(collection) > 0 && (
              <IconButton
                size="small"
                onClick={(e) => toggleCollectionExpanded(collection._id, sectionKey, e)}
                sx={{ p: 0.25, color: '#aaa', '&:hover': { color: '#072f53' } }}
              >
                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            )}
          </Box>
        </DroppableArea>
        {getAssetCount(collection) > 0 && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : assets && assets.length > 0 ? (
                <>
                  <ImageList cols={3} gap={8} sx={{ mt: 1 }}>
                    {assets.slice(0, 20).map(asset => (
                      <ImageListItem key={asset._id} sx={{ position: 'relative', pt: '100%', cursor: 'pointer' }}>
                        {asset.fileType.startsWith('image/') ? (
                          <img
                            src={
                              asset.previewPaths?.thumbnail
                                ? `${API_BASE_URL}${asset.previewPaths.thumbnail}`
                                : `${API_BASE_URL}/${asset.filePath.replace(/\\/g, '/')}`
                            }
                            alt={asset.fileName}
                            style={{
                              position: 'absolute', top: 0, left: 0,
                              width: '100%', height: '100%',
                              objectFit: 'contain', borderRadius: '4px'
                            }}
                            loading="lazy"
                            onClick={() => onCollectionSelected(collection._id)}
                          />
                        ) : (
                          <Box
                            onClick={() => onCollectionSelected(collection._id)}
                            sx={{
                              position: 'absolute', top: 0, left: 0,
                              width: '100%', height: '100%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              backgroundColor: 'action.hover', borderRadius: '4px'
                            }}
                          >
                            {asset.fileType.includes('pdf') ? '📄' :
                             asset.fileType.includes('video') ? '🎬' :
                             asset.fileType.includes('audio') ? '🎵' : '📁'}
                          </Box>
                        )}
                      </ImageListItem>
                    ))}
                  </ImageList>
                  {assets.length > 20 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                      Visar 20 av {assets.length}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 2, py: 1 }}>
                  {getAssetCount(collection) > 0 ? 'Kunde inte hämta tillgångar' : 'Inga tillgångar i denna samling'}
                </Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  onCollectionSelected(collection._id);
                }}
                sx={{ mt: 1 }}
              >
                Visa alla {getAssetCount(collection)} tillgångar
              </Button>
            </Box>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const sectionConfig: Record<string, { color: string; bg: string; hoverBg: string; badgeBg: string; icon: React.ReactNode }> = {
    pinned: {
      color: '#b8860b',
      bg: 'rgba(184,134,11,0.06)',
      hoverBg: 'rgba(184,134,11,0.10)',
      badgeBg: 'rgba(184,134,11,0.14)',
      icon: <PushPinOutlinedIcon sx={{ fontSize: 14 }} />,
    },
    recent: {
      color: '#4a7fc4',
      bg: 'rgba(74,127,196,0.06)',
      hoverBg: 'rgba(74,127,196,0.10)',
      badgeBg: 'rgba(74,127,196,0.14)',
      icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    },
    mine: {
      color: '#072f53',
      bg: 'rgba(7,47,83,0.05)',
      hoverBg: 'rgba(7,47,83,0.09)',
      badgeBg: 'rgba(7,47,83,0.12)',
      icon: <FolderIcon sx={{ fontSize: 14 }} />,
    },
    public: {
      color: '#2e7d6e',
      bg: 'rgba(46,125,110,0.06)',
      hoverBg: 'rgba(46,125,110,0.10)',
      badgeBg: 'rgba(46,125,110,0.14)',
      icon: <PublicOutlinedIcon sx={{ fontSize: 14 }} />,
    },
    other: {
      color: '#777',
      bg: 'rgba(0,0,0,0.03)',
      hoverBg: 'rgba(0,0,0,0.06)',
      badgeBg: 'rgba(0,0,0,0.08)',
      icon: <VisibilityIcon sx={{ fontSize: 14 }} />,
    },
  };

  const renderSection = (
    title: string,
    items: ICollection[],
    sectionKey: string,
    options?: {
      action?: React.ReactNode;
      emptyToggle?: { count: number; onShow: () => void };
    }
  ) => {
    if (items.length === 0 && !options?.emptyToggle) return null;
    const isSectionCollapsed = collapsedSections[sectionKey];
    const cfg = sectionConfig[sectionKey] || sectionConfig.other;

    return (
      <Box key={sectionKey} sx={{ mt: 1.5 }}>
        <Box
          onClick={() => toggleSection(sectionKey)}
          sx={{
            display: 'flex', alignItems: 'center',
            px: 1.25, py: 1,
            mx: 0.5,
            cursor: 'pointer', userSelect: 'none',
            borderLeft: `3px solid ${cfg.color}`,
            borderRadius: '0 6px 6px 0',
            bgcolor: cfg.bg,
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: cfg.hoverBg },
          }}
        >
          <Box sx={{ color: cfg.color, display: 'flex', alignItems: 'center', mr: 0.75 }}>
            {cfg.icon}
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#555',
              fontSize: '0.68rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              bgcolor: cfg.badgeBg,
              color: cfg.color,
              borderRadius: '10px',
              fontSize: '0.62rem',
              fontWeight: 700,
              minWidth: '18px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0.5,
              ml: 0.75,
            }}
          >
            {items.length}
          </Box>
          <Box sx={{ flex: 1 }} />
          {options?.action}
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              color: '#aaa',
              transform: isSectionCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.2s ease',
              ml: 0.5,
            }}
          />
        </Box>
        <Collapse in={!isSectionCollapsed}>
          <Box sx={{ pt: 0.5 }}>
            {items.map((c, idx) => (
              <React.Fragment key={`${sectionKey}-sep-${c._id}`}>
                {renderCollectionRow(c, sectionKey)}
                {idx < items.length - 1 && (
                  <Divider sx={{ mx: 2.5, borderColor: 'rgba(0,0,0,0.06)' }} />
                )}
              </React.Fragment>
            ))}
            {options?.emptyToggle && (
              <Box
                onClick={options.emptyToggle.onShow}
                sx={{
                  display: 'flex', alignItems: 'center',
                  ml: 2.5, mr: 1, my: 0.5, py: 0.5, px: 1.5,
                  cursor: 'pointer',
                  borderRadius: '6px',
                  border: '1px dashed rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.25)' },
                  transition: 'all 0.15s ease',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                  + {options.emptyToggle.count} tomma samlingar
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading collections: {error.message}
      </Alert>
    );
  }
  
  // If sidebar is collapsed, show minimal version
  if (isCollapsed) {
    return (
      <Paper
        elevation={0}
        className="collection-sidebar"
        sx={{
          height: '100%',
          overflow: 'auto',
          width: '50px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          pb: bottomInsetPx ? `${bottomInsetPx}px` : 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          ...thinScrollbarSx
        }}
      >
        <Box
          onClick={onToggleCollapsed}
          role="button"
          aria-label="Visa samlingar"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 2,
            userSelect: 'none'
          }}
        >
          <Box 
            sx={{ 
              writingMode: 'vertical-rl', 
              transform: 'rotate(180deg)', 
              color: 'text.secondary',
              fontWeight: 'medium',
              fontSize: '0.9rem',
              textTransform: 'uppercase',
              letterSpacing: 1
            }}
          >
            Samlingar
          </Box>
          <KeyboardDoubleArrowRightIcon sx={{ mt: 1, color: 'text.secondary' }} />
        </Box>
        <Divider sx={{ width: '100%', my: 1 }} />
        {collections.map(collection => {
          const isSelected = selectedCollectionId === collection._id;
          const visibilityLabel = getVisibilityLabel(collection);
          return (
            <Tooltip key={collection._id} title={`${collection.name} (${getAssetCount(collection)}) • ${visibilityLabel}`} placement="right">
              <Box
                onClick={() => handleCollectionClick(collection._id)}
                sx={{ 
                  position: 'relative',
                  my: 0.5,
                  p: 0.75,
                  borderRadius: 1,
                  cursor: 'pointer',
                  borderLeft: isSelected ? '3px solid #072f53' : '3px solid transparent',
                  bgcolor: isSelected ? 'rgba(7, 47, 83, 0.08)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <FolderIcon 
                  sx={{ 
                    fontSize: 22,
                    color: isSelected ? '#072f53' : '#666'
                  }} 
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: isSelected ? '#072f53' : '#e8e8e8',
                    color: isSelected ? 'white' : '#666',
                    borderRadius: '10px',
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    minWidth: '18px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5
                  }}
                >
                  {getAssetCount(collection) > 99 ? '99+' : getAssetCount(collection)}
                </Box>
              </Box>
            </Tooltip>
          );
        })}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Skapa ny samling">
          <IconButton onClick={onCreateCollection} sx={{ mb: 2 }}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  }
  
  return (
    <Paper 
      elevation={0}
      className="collection-sidebar"
      sx={{ 
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, color: '#333' }}>
            Samlingar
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isAdmin && (
              <Tooltip
                title={
                  adminShowAllCollections
                    ? 'Admin: visar alla samlingar (inkl privata)'
                    : 'Admin: visar allmänna + dina privata'
                }
              >
                <Switch
                  size="small"
                  checked={adminShowAllCollections}
                  onChange={(_, checked) => onAdminShowAllCollectionsChange?.(checked)}
                  sx={{ mr: 0.5 }}
                  inputProps={{ 'aria-label': 'Admin: visa alla samlingar' }}
                />
              </Tooltip>
            )}
            <Tooltip title="Dölj samlingar">
              <IconButton size="small" onClick={onToggleCollapsed} sx={{ color: '#666' }}>
                <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Skapa ny samling">
              <IconButton size="small" onClick={onCreateCollection} sx={{ color: '#072f53' }}>
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Search field */}
        <TextField
          size="small"
          placeholder="Sök samling..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              bgcolor: '#f5f5f5',
              fontSize: '0.85rem',
              '& fieldset': {
                borderColor: 'transparent'
              },
              '&:hover fieldset': {
                borderColor: '#ddd'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#072f53',
                borderWidth: 1
              }
            },
            '& .MuiOutlinedInput-input': {
              py: 1,
              px: 1.5
            }
          }}
          InputProps={{
            startAdornment: (
              <Box sx={{ color: '#999', mr: 0.5, display: 'flex', alignItems: 'center' }}>
                <FolderIcon sx={{ fontSize: 18 }} />
              </Box>
            )
          }}
        />
      </Box>
      
      <Divider sx={{ mx: 2, flexShrink: 0 }} />

      {/* Scrollable list area (header/search stays fixed) */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 1,
          pb: bottomInsetPx ? `calc(${bottomInsetPx}px + 8px)` : '8px',
          ...thinScrollbarSx
        }}
      >
        {(() => {
          if (collections.length === 0) {
            return (
              <List component="nav" dense sx={{ px: 0 }}>
                <ListItem>
                  <ListItemText
                    primary="Inga samlingar"
                    secondary="Klicka på + för att skapa en ny samling"
                  />
                </ListItem>
              </List>
            );
          }

          const filtered = collections.filter(c =>
            c.name.toLowerCase().includes(searchQuery.toLowerCase())
          );

          if (filtered.length === 0 && searchQuery) {
            return (
              <List component="nav" dense sx={{ px: 0 }}>
                <ListItem>
                  <ListItemText
                    primary="Inga träffar"
                    secondary={`Ingen samling matchar "${searchQuery}"`}
                    primaryTypographyProps={{ color: 'text.secondary', fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItem>
              </List>
            );
          }

          const pinnedSet = new Set(pinnedCollectionIds);

          const pinnedItems = pinnedCollectionIds
            .map(id => filtered.find(c => c._id === id))
            .filter((c): c is ICollection => !!c);

          const recentItems = recentlyUsedIds
            .filter(id => !pinnedSet.has(id))
            .map(id => filtered.find(c => c._id === id))
            .filter((c): c is ICollection => !!c)
            .slice(0, 5);

          const myAll = sortCollections(
            filtered.filter(c => isOwnedByMe(c) && !isEffectivelyPublic(c))
          );
          const GRACE_MS = 24 * 60 * 60 * 1000;
          const alwaysShow = (c: ICollection) =>
            getAssetCount(c) > 0 ||
            (Date.now() - new Date(c.createdAt).getTime()) < GRACE_MS ||
            c._id === selectedCollectionId;
          const myNonEmpty = myAll.filter(alwaysShow);
          const myEmpty = myAll.filter(c => !alwaysShow(c));
          const myVisible = showEmptyInMine ? myAll : myNonEmpty;

          const publicCollections = sortCollections(
            filtered.filter(c => isEffectivelyPublic(c))
          );

          const otherPrivate = adminShowAllCollections
            ? sortCollections(filtered.filter(c => !isOwnedByMe(c) && !isEffectivelyPublic(c)))
            : [];

          return (
            <Box sx={{ px: 0 }}>
              {renderSection('Fästa', pinnedItems, 'pinned')}
              {renderSection('Senast använda', recentItems, 'recent')}
              {renderSection('Mina samlingar', myVisible, 'mine', {
                action: (
                  <>
                    <Tooltip title={`Sortering: ${sortLabel[sortMode]}`}>
                      <IconButton
                        size="small"
                        onClick={cycleSortMode}
                        sx={{ p: 0.25, color: sortMode === 'name' ? '#aaa' : '#072f53' }}
                      >
                        {sortMode === 'name'
                          ? <SortByAlphaIcon sx={{ fontSize: 15 }} />
                          : <SortIcon sx={{ fontSize: 15 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Skapa ny samling">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onCreateCollection(); }}
                        sx={{ p: 0.25, color: '#072f53' }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                ),
                emptyToggle: !showEmptyInMine && myEmpty.length > 0
                  ? { count: myEmpty.length, onShow: () => setShowEmptyInMine(true) }
                  : undefined,
              })}
              {renderSection('Allmänna', publicCollections, 'public', {
                action: (
                  <Tooltip title={`Sortering: ${sortLabel[sortMode]}`}>
                    <IconButton
                      size="small"
                      onClick={cycleSortMode}
                      sx={{ p: 0.25, color: sortMode === 'name' ? '#aaa' : '#072f53' }}
                    >
                      {sortMode === 'name'
                        ? <SortByAlphaIcon sx={{ fontSize: 15 }} />
                        : <SortIcon sx={{ fontSize: 15 }} />}
                    </IconButton>
                  </Tooltip>
                ),
              })}
              {adminShowAllCollections && otherPrivate.length > 0 &&
                renderSection('Övriga (admin)', otherPrivate, 'other')}
            </Box>
          );
        })()}
      </Box>

      {/* Context menu for collections */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => { setMenuAnchorEl(null); setMenuCollectionId(null); }}
        MenuListProps={{
          dense: true,
          sx: { py: 1 }
        }}
        PaperProps={{ 
          sx: { 
            width: 220, 
            borderRadius: '8px',
            bgcolor: '#f8f8f8',
            border: '1px solid rgba(0,0,0,0.08)'
          } 
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuCollectionId) togglePin(menuCollectionId);
            setMenuAnchorEl(null);
            setMenuCollectionId(null);
          }}
          sx={{ py: 1, px: 2, minHeight: 0 }}
        >
          <MListItemIcon sx={{ minWidth: 28 }}>
            <PushPinOutlinedIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
          </MListItemIcon>
          <MListItemText
            primary={menuCollectionId && pinnedCollectionIds.includes(menuCollectionId) ? 'Lösgör' : 'Fäst samling'}
            primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }}
          />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            const c = collections.find(c => c._id === menuCollectionId);
            if (c) {
              setRenameValue(c.name);
              setRenameDescription((c as any).description ?? '');
              setRenameOpen(true);
            }
            setMenuAnchorEl(null);
          }}
          sx={{ py: 1, px: 2, minHeight: 0 }}
        >
          <MListItemIcon sx={{ minWidth: 28 }}><EditIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} /></MListItemIcon>
          <MListItemText primary="Ändra namn / beskrivning" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            const c = collections.find(c => c._id === menuCollectionId);
            if (c) onShareCollection(c);
            setMenuAnchorEl(null);
            setMenuCollectionId(null);
          }}
          sx={{ py: 1, px: 2, minHeight: 0 }}
        >
          <MListItemIcon sx={{ minWidth: 28 }}><SettingsIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} /></MListItemIcon>
          <MListItemText primary="Ändra inställningar" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (!menuCollectionId) {
              setMenuAnchorEl(null);
              setMenuCollectionId(null);
              return;
            }
            try {
              const resp = await axiosInstance.get(`/api/share/collections/${menuCollectionId}/share`);
              const shareToken = resp.data?.shareLink?.shareToken;
              if (shareToken) {
                const shareUrl = `${window.location.origin}/share/${shareToken}`;
                await navigator.clipboard.writeText(shareUrl);
                // Could add a toast notification here if needed
              }
            } catch (err) {
              console.error('Could not copy share link:', err);
            } finally {
              setMenuAnchorEl(null);
              setMenuCollectionId(null);
            }
          }}
          sx={{ py: 1, px: 2, minHeight: 0 }}
        >
          <MListItemIcon sx={{ minWidth: 28 }}><ContentCopyIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} /></MListItemIcon>
          <MListItemText primary="Kopiera länk" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (!menuCollectionId) {
              setMenuAnchorEl(null);
              setMenuCollectionId(null);
              return;
            }
            try {
              // Get the share link for this collection (auto-created if needed)
              const resp = await axiosInstance.get(`/api/share/collections/${menuCollectionId}/share`);
              const shareToken = resp.data?.shareLink?.shareToken;
              if (shareToken) {
                window.open(`${window.location.origin}/share/${shareToken}`, '_blank', 'noopener,noreferrer');
              }
            } catch (err) {
              console.error('Could not get share link:', err);
            } finally {
              setMenuAnchorEl(null);
              setMenuCollectionId(null);
            }
          }}
          sx={{ py: 1, px: 2, minHeight: 0 }}
        >
          <MListItemIcon sx={{ minWidth: 28 }}><OpenInNewIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} /></MListItemIcon>
          <MListItemText primary="Förhandsvisa delad sida" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
        </MenuItem>
        <MenuItem
          onClick={async () => {
            const c = collections.find(c => c._id === menuCollectionId);
            if (!c) {
              setMenuAnchorEl(null);
              setMenuCollectionId(null);
              return;
            }
            const currentlyPublic = isEffectivelyPublic(c);
            const nextPublicInternal = !currentlyPublic;
            try {
              const resp = await axiosInstance.put(`/api/collections/${c._id}`, { isPublicInternal: nextPublicInternal });
              const updated = resp.data || {};
              const idx = collections.findIndex(x => x._id === c._id);
              if (idx > -1) {
                (collections[idx] as any).isPublicInternal = Boolean(updated.isPublicInternal);
                if (updated.owner) (collections[idx] as any).owner = updated.owner;
              }
              forceUpdate(n => n + 1);
            } catch (err) {
              console.error('Toggle collection visibility failed:', err);
            } finally {
              setMenuAnchorEl(null);
              setMenuCollectionId(null);
            }
          }}
          sx={{ py: 1, px: 2, minHeight: 0 }}
        >
          <MListItemIcon sx={{ minWidth: 28 }}>
            {(() => {
              const c = collections.find(c => c._id === menuCollectionId);
              const currentlyPublic = c ? isEffectivelyPublic(c) : false;
              return currentlyPublic ? (
                <LockOutlinedIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
              ) : (
                <PublicOutlinedIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
              );
            })()}
          </MListItemIcon>
          <MListItemText
            primary={(() => {
              const c = collections.find(c => c._id === menuCollectionId);
              const currentlyPublic = c ? isEffectivelyPublic(c) : false;
              return currentlyPublic ? 'Gör privat' : 'Gör allmän';
            })()}
            primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }}
          />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            const c = collections.find(c => c._id === menuCollectionId);
            if (c) onDeleteCollection(c);
            setMenuAnchorEl(null);
            setMenuCollectionId(null);
          }}
          sx={{ py: 1, px: 2, minHeight: 0 }}
        >
          <MListItemIcon sx={{ minWidth: 28 }}><DeleteIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} /></MListItemIcon>
          <MListItemText primary="Ta bort" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
        </MenuItem>
      </Menu>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)}>
        <DialogTitle>Ändra namn / beskrivning</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            placeholder="Beskrivning"
            value={renameDescription ?? ''}
            onChange={(e) => setRenameDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const c = collections.find(c => c._id === menuCollectionId);
              if (!c) { setRenameOpen(false); return; }
              try {
                await axiosInstance.put(`/api/collections/${c._id}`, { name: renameValue.trim(), description: (renameDescription ?? '').trim() });
                // Uppdatera det lokala namnet direkt (optimistiskt)
                const idx = collections.findIndex(x => x._id === c._id);
                if (idx > -1) {
                  collections[idx].name = renameValue.trim();
                  (collections[idx] as any).description = (renameDescription ?? '').trim();
                }
                setRenameOpen(false);
              } catch (err) {
                console.error('Rename collection failed:', err);
                setRenameOpen(false);
              }
            }}
            disabled={!renameValue.trim()}
          >
            Spara
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CollectionSidebar; 